/**
 * HustleSuite legal pages — privacy policy + terms of use.
 *
 * Rendered at /legal/privacy and /legal/terms (routed in App.js). These
 * are the canonical public legal pages linked from the App Store
 * Connect App Privacy config, so they must always return real content
 * regardless of hostname or auth state.
 */
import React from "react";
import { Link } from "react-router-dom";

const container = {
  maxWidth: 780,
  margin: "0 auto",
  padding: "64px 24px 96px",
  fontFamily: "-apple-system, Segoe UI, Roboto, Arial, sans-serif",
  color: "#111827",
  lineHeight: 1.65,
  fontSize: 16,
};

const h1 = { fontSize: 32, margin: "0 0 8px", fontWeight: 700 };
const h2 = { fontSize: 20, margin: "36px 0 8px", fontWeight: 600 };
const meta = { color: "#6b7280", fontSize: 14, marginBottom: 32 };
const p = { margin: "0 0 14px" };
const ul = { margin: "0 0 14px 20px", padding: 0 };

const CONTACT_EMAIL = "privacy@hustlesuite.au";
const OPERATOR = "LOCAL HUSTLE (ABN 16 160 565 775)";
const JURISDICTION = "Queensland, Australia";
const LAST_UPDATED = "12 July 2026";

export default function HustleSuitePrivacyPage() {
  return (
    <div style={container} data-testid="hustlesuite-privacy-page">
      <h1 style={h1}>HustleSuite Privacy Policy</h1>
      <p style={meta}>Last updated: {LAST_UPDATED}</p>

      <p style={p}>
        HustleSuite ("we", "us", "the app") is operated by {OPERATOR}. This
        Privacy Policy explains what personal information we collect when
        you use HustleSuite (including its native mobile app and every
        module hosted at <code>hustlesuite.au</code>, <code>hustleplaza.au</code>,{" "}
        <code>tackle.au</code>, <code>hustleproperties.au</code>,{" "}
        <code>hustleeats.au</code>, and related sub-domains), why we
        collect it, and how we handle it under the Australian Privacy Act
        1988 (Cth), the Australian Privacy Principles, the EU GDPR (where
        applicable), and Apple's App Store guidelines.
      </p>

      <h2 style={h2}>1. Information we collect</h2>
      <p style={p}>We only collect data necessary to run our modules for you:</p>
      <ul style={ul}>
        <li><strong>Account:</strong> name, email, phone number (optional), password (hashed only)</li>
        <li><strong>Financial info:</strong> payment tokens issued by Stripe — we never see full card numbers</li>
        <li><strong>Location:</strong> precise location when you grant permission (for flood reports, near-me searches, property viewings). You can revoke at any time in device Settings.</li>
        <li><strong>User content:</strong> reviews, listings, photos, voice notes, chat messages you submit</li>
        <li><strong>Identifiers:</strong> your internal user ID and a device identifier used only for push-notification routing</li>
        <li><strong>Search history:</strong> your searches within HustleSuite modules, retained to improve recommendations</li>
        <li><strong>Purchase history:</strong> orders you place through Plaza, Tackle, Eats, and other commerce modules</li>
        <li><strong>Usage data:</strong> product interactions (clicks, page views) for analytics</li>
        <li><strong>Diagnostics:</strong> crash reports and performance data, sent to our own servers only</li>
      </ul>

      <h2 style={h2}>2. Information we do NOT collect</h2>
      <ul style={ul}>
        <li>Race, religion, sexual orientation, disability, or other sensitive personal data</li>
        <li>Your device contact list</li>
        <li>Your browsing history outside HustleSuite</li>
        <li>Your health, fitness, or biometric data</li>
        <li>Any data used for cross-app or third-party advertising</li>
      </ul>

      <h2 style={h2}>3. How we use your data</h2>
      <ul style={ul}>
        <li>To provide the modules you use (Plaza cart, Property listings, Tackle shop, Eats bookings, etc.)</li>
        <li>To personalise recommendations, saved searches, and re-order shortcuts</li>
        <li>To send transactional emails, SMS, and push notifications you've enabled</li>
        <li>To detect and prevent fraud, spam, and abuse</li>
        <li>To improve product performance via aggregate analytics</li>
      </ul>

      <h2 style={h2}>4. Third parties we share limited data with</h2>
      <p style={p}>
        We use trusted service providers strictly for delivering our own
        service — never for their own marketing, advertising, or data
        brokerage:
      </p>
      <ul style={ul}>
        <li><strong>Stripe</strong> — payment processing</li>
        <li><strong>Twilio</strong> — transactional SMS</li>
        <li><strong>Resend</strong> — transactional email</li>
        <li><strong>Cloudflare</strong> — CDN + DDoS protection</li>
        <li><strong>Apple / Google</strong> — push notification delivery only (via APNs and Firebase Cloud Messaging)</li>
        <li><strong>MongoDB Atlas</strong> — hosted database</li>
        <li><strong>DigitalOcean</strong> — server hosting (data resides in Sydney, Australia)</li>
      </ul>

      <h2 style={h2}>5. Where your data lives</h2>
      <p style={p}>
        Primary database servers are located in Sydney, Australia. Some
        third-party services (Stripe, Resend, Cloudflare) process limited
        data in the US and EU under contractual Standard Contractual
        Clauses.
      </p>

      <h2 style={h2}>6. How long we keep your data</h2>
      <ul style={ul}>
        <li>Active account data: as long as your account is active</li>
        <li>Order records: 7 years (Australian tax law requirement)</li>
        <li>Diagnostic logs: 90 days</li>
        <li>Deleted accounts: purged within 30 days of a verified deletion request, except records legally required to retain</li>
      </ul>

      <h2 style={h2}>7. Your rights</h2>
      <p style={p}>Under Australian privacy law you have the right to:</p>
      <ul style={ul}>
        <li>Access the personal data we hold about you</li>
        <li>Correct data that is inaccurate</li>
        <li>Request permanent deletion of your account and associated data</li>
        <li>Opt out of any marketing communications (transactional emails are exempt)</li>
        <li>Complain to the Office of the Australian Information Commissioner (oaic.gov.au) if you believe we've mishandled your data</li>
      </ul>
      <p style={p}>
        To exercise any of these rights email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        We respond within 30 days.
      </p>

      <h2 style={h2}>8. Cookies and tracking</h2>
      <p style={p}>
        HustleSuite uses first-party session cookies to keep you signed in.
        We do not use third-party advertising cookies. We do not track
        your activity across other companies' apps or websites.
      </p>

      <h2 style={h2}>9. Children</h2>
      <p style={p}>
        HustleSuite is not directed at children under 13 years of age. We
        do not knowingly collect personal data from children under 13. If
        you believe a child has provided us data, contact us at{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will
        delete it.
      </p>

      <h2 style={h2}>10. Changes to this policy</h2>
      <p style={p}>
        We may update this Privacy Policy from time to time. Material
        changes will be notified via email or in-app banner at least 14
        days before they take effect. The current version and its
        effective date are always shown at the top of this page.
      </p>

      <h2 style={h2}>11. Governing law</h2>
      <p style={p}>
        This policy is governed by the laws of {JURISDICTION}, Australia.
      </p>

      <h2 style={h2}>12. Contact</h2>
      <p style={p}>
        For any privacy question or request:{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </p>

      <p style={{ ...meta, marginTop: 48 }}>
        See also our{" "}
        <Link to="/legal/terms" style={{ color: "#c55a48" }}>
          Terms of Use
        </Link>
        .
      </p>
    </div>
  );
}
